import React, { useMemo } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import styled from 'styled-components/macro';
import { dndConstants } from 'store/constants';
import { EachPageWithThumbnail } from 'store/models/file-selected/file-selected.interface';
import { useDispatch } from 'react-redux';
import {
  // renderPreviewCover,
  renderPreviewFab,
  SPage,
  SThumbnail,
  THUMBNAIL_HEIGHT,
  THUMBNAIL_WIDTH,
} from './SrcThumbnail';
import { FormattedMainFile } from 'store/models/MainFiles';
import {
  commonPagesActions,
  fileSelectedActions,
} from 'store/actions';
import { Badge, IconButton } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';

interface DestThumbnailPageProps {
  thumbnail?: EachPageWithThumbnail;
  destPageNo: number;
  totalPages?: number;
  destFileIndex?: number; // if is undefined then assume is global common pages
  index?: number;
  allUrls?: string[];
}

const DestThumbnail: React.FC<DestThumbnailPageProps> = ({
  thumbnail,
  destPageNo,
  totalPages,
  destFileIndex,
  index, // thumbnail index
  allUrls,
}) => {
  const dispatch = useDispatch();
  const pageIndex = useMemo(() => destPageNo - 1, [destPageNo]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [{ isDragging }, drag] = useDrag({
    item: {
      type: dndConstants.DEST_PAGE,
      payload: {
        oldFileIndex: destFileIndex, //if undefined then it is from common page
        oldPageIndex: pageIndex,
      },
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });
  const [{ isOver }, drop] = useDrop({
    accept: [dndConstants.SRC_PAGE, dndConstants.DEST_PAGE],
    drop: (
      item: {
        type: string;
        payload:
          | { srcFile: FormattedMainFile; pageNum: number }
          | { oldFileIndex: number; oldPageIndex: number };
      },
      monitor,
    ) => {
      const { payload: payloadStart } = item;
      if (item.type === dndConstants.SRC_PAGE) {
        const payload = payloadStart as {
          srcFile: FormattedMainFile;
          pageNum: number;
        };
        if (!payload.srcFile || !payload.pageNum)
          throw Error(`missing payload.srcFile or payload.pageNum`);
        handleSelectPage(payload.srcFile, payload.pageNum);
      } else if (item.type === dndConstants.DEST_PAGE) {
        const payload = payloadStart as {
          oldFileIndex: number;
          oldPageIndex: number;
        };
        if (destFileIndex !== undefined) {
          dispatch(
            fileSelectedActions.movePage(
              payload.oldFileIndex,
              payload.oldPageIndex,
              destFileIndex,
              pageIndex,
            ),
          );
        } else {
          if (payload.oldFileIndex !== undefined) {
            console.warn(
              `doesn't work with page outside of common pages for now`,
            );
          } else {
            dispatch(
              commonPagesActions.movePage(
                payload.oldPageIndex,
                pageIndex,
              ),
            );
          }
        }
      }
    },
    canDrop: (item) => {
      let canDrop = true;
      if (destFileIndex === undefined) {
        // common block
        canDrop =
          item.type !== dndConstants.DEST_PAGE ||
          (item.payload as {
            oldFileIndex: number;
            oldPageIndex: number;
          })?.oldFileIndex === undefined; // accept only from within the common
      }
      return canDrop;
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver() && !!monitor.canDrop(),
    }),
  });
  // useEffect(() => {
  //   console.debug({ DestThumbnailIsOver: isOver });
  // }, [isOver]);

  const handleSelectPage = (
    att: FormattedMainFile,
    pageNum: number,
  ) => {
    if (pageNum < 1) throw Error(`page num should be >= 1`);
    console.debug(`select page: `, pageNum, 'partId: ', att.partId);
    if (destFileIndex !== undefined) {
      // not global common page
      dispatch(
        fileSelectedActions.addPageToAttIndexAfterPageIndex(
          att,
          pageNum,
          destFileIndex,
          pageIndex,
        ),
      );
    } else {
      // global common page
      dispatch(
        commonPagesActions.addPageAfterPageIndex(
          att.partId,
          pageNum,
          pageIndex,
        ),
      );
    }
  };
  const handleRemovePage = () => {
    if (destFileIndex !== undefined) {
      dispatch(
        fileSelectedActions.removePage(destFileIndex, pageIndex),
      );
    } else {
      dispatch(commonPagesActions.removePage(pageIndex));
    }
  };

  const renderFromGlobalCommonPage = (page?: number) => {
    if (page === undefined) return null;
    return <SBadge badgeContent={page} color="primary"></SBadge>;
  };

  return (
    <SThumbnailAndPageAndDrop
      key={destPageNo}
      ref={drop}
      // className={!thumbnail ? 'pos-abs' : ''}
    >
      {!thumbnail ? (
        <SInvisibleDrop />
      ) : (
        <SThumbnailAndPage ref={drag} className="moveable">
          {/* <SDeletePageIcon /> */}
          <SRemovePageIcon size="small" onClick={handleRemovePage}>
            <CloseIcon fontSize="small" />
          </SRemovePageIcon>
          <SThumbnail
            key={thumbnail.pageNo}
            className={
              thumbnail.fromGlobalCommonPage !== undefined
                ? 'from-global'
                : ''
            }
          >
            {renderFromGlobalCommonPage(
              thumbnail.fromGlobalCommonPage,
            )}
            {/* {renderPreviewCover(
              dispatch,
              thumbnail.thumbnail,
              undefined,
              false,
            )} */}
            <img
              src={thumbnail.thumbnail?.url}
              alt={`${thumbnail.pageNo}`}
            />
            {renderPreviewFab(dispatch, index, allUrls)}
          </SThumbnail>
          <SPage>{`${destPageNo}/${totalPages}`}</SPage>
        </SThumbnailAndPage>
      )}
      {isOver && (
        <SDropPlaceHolder
          className={totalPages === destPageNo ? 'last-index' : ''}
        />
      )}
    </SThumbnailAndPageAndDrop>
  );
};

export default DestThumbnail;

const SRemovePageIcon = styled(IconButton)`
  &.MuiButtonBase-root {
    opacity: 0;
    z-index: 1;
    position: absolute;
    right: 0;
    transition: opacity 0.3s;
  }
`;

const SThumbnailAndPage = styled.div`
  position: relative;
  &:hover > ${SRemovePageIcon}.MuiButtonBase-root {
    opacity: 100;
  }
`;
export const SDropPlaceHolder = styled.div`
  border: dashed 2px ${(props) => props.theme.palette.primary.main};
  background: ${(props) => props.theme.palette.highlight.transPink};
  height: ${THUMBNAIL_HEIGHT};
  margin: 0 5px 0 0;
  width: ${THUMBNAIL_WIDTH};
  &.last-index {
    margin-right: 0;
  }
`;
const SThumbnailAndPageAndDrop = styled.div`
  & > * {
    display: inline-block;
  }
  display: flex;
  &.pos-abs {
    position: absolute;
  }
`;
const SInvisibleDrop = styled.div`
  height: ${THUMBNAIL_HEIGHT};
  margin: 0 5px 0 0;
  width: ${THUMBNAIL_WIDTH};
  position: absolute;
`;
const SBadge = styled(Badge)`
  &.MuiBadge-root {
    position: absolute;
    top: 8px;
    left: 8px;
  }
`;
